import upperFirst from 'lodash/upperFirst'
import React, { forwardRef, useImperativeHandle, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Provider, PolicyAggregation, DomainPermission_VerbEnum, DomainPermission_DomainEnum } from 'src/api/gen/api'
import { getPolicyKindEntries } from 'src/commons/filters/utils'
import { sortObjectsByLabel } from 'src/commons/helpers'
import useFeeRates from 'src/commons/hooks/useFeeRates'
import { enumsTranslationKeys, withFormattedCount } from 'src/commons/i18nEnums'
import { Can } from 'src/commons/permissions'
import { IPoliciesListFilters } from 'src/commons/policies/policies'
import { SidePanelButton } from 'src/components/SidePanel'
import { useProviders, useProviderUser } from 'src/layout/hooks'
import CheckboxListFilter from 'src/muiv5/components/commons/SidePanel/Filters/CheckboxListFilter/CheckboxListFilter'
import { SidePanel } from 'src/muiv5/components/commons/SidePanel/SidePanel'

export const usePolicyStatus = (policiesAggregations: PolicyAggregation) => {
  const { t } = useTranslation()
  const isProviderUser = useProviderUser()

  const policyStatus = isProviderUser
    ? Object.entries(enumsTranslationKeys.PolicyStatus).filter(([id, _]) => id !== 'draft')
    : Object.entries(enumsTranslationKeys.PolicyStatus)

  const statusEntries = policyStatus.map(([id, name]) => ({
    id,
    label: upperFirst(t(name)),
    value: policiesAggregations.statuses?.[id],
  }))

  return statusEntries
}

interface IFeeRatesFilterPanelProps {
  defaultFilters: IPoliciesListFilters
  initialFilters: IPoliciesListFilters
  onNewPolicyClick: () => void
  policiesAggregation: PolicyAggregation
  policiesCount: number
}

const FeeRatesFilterPanel = forwardRef(function (props: IFeeRatesFilterPanelProps, ref) {
  const { t } = useTranslation()
  const { activeProviders } = useProviders()
  const policyKinds = useFeeRates()
  const statusEntries = usePolicyStatus(props.policiesAggregation)

  const [filters, setFilters] = useState(props.initialFilters)

  useImperativeHandle(ref, () => filters, [filters])

  function onFilterChange(
    filterKey: keyof IPoliciesListFilters,
    filterValue: IPoliciesListFilters[keyof IPoliciesListFilters]
  ) {
    setFilters({ ...filters, [filterKey]: filterValue })
  }

  let providerEntries = null

  if (activeProviders.length > 1) {
    providerEntries = activeProviders
      .map((provider: Provider) => ({
        id: provider.id,
        label: provider.name,
        value: props.policiesAggregation.providers[provider.id],
      }))
      .sort(sortObjectsByLabel)
  }

  return (
    <SidePanel
      headerProps={{
        title: t('filtersPanel.title.default'),
        information: t('titles.fee_rates.count', withFormattedCount(props.policiesCount)),
        actions: (
          <Can I={DomainPermission_VerbEnum.write} a={DomainPermission_DomainEnum.fee_rates}>
            <SidePanelButton label={upperFirst(t('buttons.create.fee_rates'))} onClick={props.onNewPolicyClick} />
          </Can>
        ),
        onReset: () => setFilters(props.defaultFilters),
      }}
    >
      <CheckboxListFilter
        filterId={'status'}
        items={statusEntries}
        values={filters.status}
        title={upperFirst(t('filters.status.fee_rates'))}
        onFilterChange={(key: string, newValue: string[]) => {
          onFilterChange(key as keyof IPoliciesListFilters, newValue)
        }}
        displayCheckbox
      />
      <CheckboxListFilter
        filterId={'kind'}
        items={getPolicyKindEntries(policyKinds, props.policiesAggregation.kinds)}
        values={filters.kind}
        title={upperFirst(t('filters.kind.fee_rates'))}
        onFilterChange={(key: string, newValue: string[]) => {
          onFilterChange(key as keyof IPoliciesListFilters, newValue)
        }}
        displayCheckbox
      />
      {providerEntries && (
        <CheckboxListFilter
          filterId={'providers'}
          items={providerEntries}
          values={filters.providers}
          title={upperFirst(t('filters.provider'))}
          onFilterChange={(key: string, newValue: string[]) => {
            console.log(key, newValue)
            onFilterChange(key as keyof IPoliciesListFilters, newValue)
          }}
          displayCheckbox
        />
      )}
    </SidePanel>
  )
})

export default FeeRatesFilterPanel
